<template>
  <span>
    <v-row dense justify="center">
      <v-col cols="12" v-for="chart in dataCharts" :key="chart.title">
        <v-card-charts-dashboard
          :title="chart.title"
          :tooltip-text="chart.tooltipText"
          :options="chart.options"
          :deep-copy-on-update="false"
          :is-loading="chart.loadingKey"
        />
      </v-col>
    </v-row>
  </span>
</template>

<script>
import VCardChartsDashboard from "@/components/base/VCardChartsDashboard.vue";
import DashboardRessarcimentoDanosEletricosService from "@/services/DashboardRessarcimentoDanosEletricosService";

import Highcharts from "highcharts";
import HighchartsNoData from "highcharts/modules/no-data-to-display";
HighchartsNoData(Highcharts);
import exporting from "highcharts/modules/exporting";
import exportData from "highcharts/modules/export-data";
import dayjs from "dayjs";

exporting(Highcharts);
exportData(Highcharts);
export default {
  name: "DashboardRessarcimentoDanosEletricosVisaoGeral",
  components: {
    VCardChartsDashboard,
  },
  props: {
    competencia: {
      type: String,
      required: true,
    },
    activatedTab: {
      type: Number,
      required: true,
    },
    regionalSelecionada: {
      type: Number,
      default: 0,
    },
    visaoGraficos: {
      type: String,
      default: "mensal",
    },
  },
  data() {
    return {
      dadosTotais: {},
      dadosValorPendenteSolicitacoesDeferidas: {},
      dadosValorPagoRessarcimentoDanosEletricos: {},
      dadosValorPendenteSolicitacoesDeferidasAgrupados: {},
      dadosValorPagoRessarcimentoDanosEletricosAgrupados: {},
      dadosTotaisRessarcimentoDanosEletricosAgrupados: {},
      dadosAgrupadosPorAno: {},
      anosDisponiveis: [],
      chartOptionsPercentualSolicitacoesRessarcimentoNegadas: {},
      chartOptionsQtdeSolicitacoesRessarcimentoPorDanosEletricos: {},
      chartOptionsQtdeSolicitacoesIndeferidasPorDanosEletricos: {},
      chartOptionsValorTotalPendenteDeferido: {},
      chartOptionsValorTotalPagoPorRessarcimento: {},
      debounceTimer: null,
      isLoadingDados: false,
      loadingPorChave: {
        dadosTotais: false,
        dadosValorPendenteSolicitacoesDeferidas: false,
        dadosValorPagoRessarcimentoDanosEletricos: false,
      },
      explicacoes: {
        percentualNegadas:
          "O percentual de solicitações de ressarcimento negadas é calculado a partir da razão entre a quantidade de solicitações de ressarcimento por danos elétricos indeferidas e o total de solicitações de ressarcimento por danos elétricos. Ressalta-se que as solicitações indeferidas podem não corresponder exatamente às mesmas que foram protocoladas no mês de referência, o que pode gerar distorções no indicador — especialmente quanto menor for a janela de agregação considerada.",
        quantidadeRessarcimento: "",
        quantidadeIndeferidas: "",
        valorPendenteDeferido: "",
        valorPagoRessarcimento: "",
      },
      dataChartsConfig: [
        {
          title: "Percentual de solicitações de ressarcimento negadas (%)",
          tooltipKey: "percentualNegadas",
          chartOptionsKey:
            "chartOptionsPercentualSolicitacoesRessarcimentoNegadas",
          loadingKey: "dadosTotais",
        },
        {
          title:
            "Quantidade de solicitações de ressarcimento por danos elétricos",
          tooltipKey: "quantidadeRessarcimento",
          chartOptionsKey:
            "chartOptionsQtdeSolicitacoesRessarcimentoPorDanosEletricos",
          loadingKey: "dadosTotais",
        },
        {
          title: "Quantidade de solicitações indeferidas por danos elétricos",
          tooltipKey: "quantidadeIndeferidas",
          chartOptionsKey:
            "chartOptionsQtdeSolicitacoesIndeferidasPorDanosEletricos",
          loadingKey: "dadosTotais",
        },
        {
          title: "Valor Total Pendente Deferido (R$)",
          tooltipKey: "valorPendenteDeferido",
          chartOptionsKey: "chartOptionsValorTotalPendenteDeferido",
          loadingKey: "dadosValorPendenteSolicitacoesDeferidas",
        },
        {
          title: "Valor Total Pago por Ressarcimento (R$)",
          tooltipKey: "valorPagoRessarcimento",
          chartOptionsKey: "chartOptionsValorTotalPagoPorRessarcimento",
          loadingKey: "dadosValorPagoRessarcimentoDanosEletricos",
        },
      ],
    };
  },
  computed: {
    dataCharts() {
      return this.dataChartsConfig.map((config) => ({
        title: config.title,
        tooltipText: this.explicacoes[config.tooltipKey],
        options: this[config.chartOptionsKey],
        loadingKey: this.loadingPorChave[config.loadingKey],
      }));
    },
  },
  mounted() {
    this.getDados();
  },
  beforeDestroy() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    this.limparGraficos();
  },
  methods: {
    getDados() {
      if (this.activatedTab !== 0 || this.isLoadingDados) return;

      this.isLoadingDados = true;
      this.$emit("updateLoadingDados", true);

      Promise.allSettled([
        this.getDadosTotais(),
        this.getDadosValorPendenteSolicitacoesDeferidas(),
        this.getDadosValorPagoRessarcimentoDanosEletricos(),
      ])
        .then((results) => {
          const hasError = results.some((r) => r.status === "rejected");
          if (hasError) {
            this.$toast.error("Erro ao recuperar dados.", "", {
              position: "topRight",
            });
          }
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          this.isLoadingDados = false;
          this.$emit("updateLoadingDados", false);
        });
    },
    getDadosComDebounce() {
      if (this.activatedTab !== 0) return;

      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      this.$emit("updateLoadingDados", true);

      this.debounceTimer = setTimeout(() => {
        this.getDados();
      }, 500);
    },

    getDadosTotais() {
      this.loadingPorChave.dadosTotais = true;
      const anoAnterior = dayjs(this.competencia)
        .subtract(1, "year")
        .format("YYYY-01");
      const anoAtual = dayjs(this.competencia).format("YYYY-12");

      const serviceMethod =
        this.visaoGraficos === "acumulados"
          ? DashboardRessarcimentoDanosEletricosService.getDadosTotaisRessarcimentoDanosEletricosAcumulados
          : DashboardRessarcimentoDanosEletricosService.getDadosTotaisRessarcimentoDanosEletricos;

      return serviceMethod(anoAnterior, anoAtual, this.regionalSelecionada)
        .then((res) => {
          this.dadosTotais = res;
          this.processarDadosParaGraficos();
        })
        .finally(() => {
          this.loadingPorChave.dadosTotais = false;
        })
        .catch((err) => {
          throw err;
        });
    },

    getDadosValorPendenteSolicitacoesDeferidas() {
      this.loadingPorChave.dadosValorPendenteSolicitacoesDeferidas = true;
      const anoAnterior = dayjs(this.competencia)
        .subtract(1, "year")
        .format("YYYY-01");
      const anoAtual = dayjs(this.competencia).format("YYYY-12");

      const serviceMethod =
        this.visaoGraficos === "acumulados"
          ? DashboardRessarcimentoDanosEletricosService.getValorPendenteSolicitacoesDeferidasAcumulados
          : DashboardRessarcimentoDanosEletricosService.getValorPendenteSolicitacoesDeferidas;

      return serviceMethod(anoAnterior, anoAtual, this.regionalSelecionada)
        .then((res) => {
          this.dadosValorPendenteSolicitacoesDeferidas = res;
          this.processarGraficosValores();
        })
        .catch((err) => {
          throw err;
        })
        .finally(() => {
          this.loadingPorChave.dadosValorPendenteSolicitacoesDeferidas = false;
        });
    },

    getDadosValorPagoRessarcimentoDanosEletricos() {
      this.loadingPorChave.dadosValorPagoRessarcimentoDanosEletricos = true;
      const anoAnterior = dayjs(this.competencia)
        .subtract(1, "year")
        .format("YYYY-01");
      const anoAtual = dayjs(this.competencia).format("YYYY-12");

      const serviceMethod =
        this.visaoGraficos === "acumulados"
          ? DashboardRessarcimentoDanosEletricosService.getValorPagoRessarcimentoDanosEletricosAcumulados
          : DashboardRessarcimentoDanosEletricosService.getValorPagoRessarcimentoDanosEletricos;

      return serviceMethod(anoAnterior, anoAtual, this.regionalSelecionada)
        .then((res) => {
          this.dadosValorPagoRessarcimentoDanosEletricos = res;
          this.processarGraficosValores();
        })
        .catch((err) => {
          console.error(err);
          throw err;
        })
        .finally(() => {
          this.loadingPorChave.dadosValorPagoRessarcimentoDanosEletricos = false;
        });
    },

    processarDadosParaGraficos() {
      if (!this.dadosTotais || Object.keys(this.dadosTotais).length === 0) {
        this.limparGraficos();
        return;
      }

      this.agruparDadosPorAno();
      this.processarGraficosComparativos();
    },

    agruparDadosPorAno() {
      const dadosAgrupados = {};
      const anos = new Set();

      Object.entries(this.dadosTotais).forEach(([periodo, dados]) => {
        const [mes, ano] = periodo.split("/").map(Number);
        anos.add(ano);

        if (!dadosAgrupados[ano]) dadosAgrupados[ano] = {};
        dadosAgrupados[ano][mes] = dados;
      });

      this.dadosAgrupadosPorAno = dadosAgrupados;
      this.anosDisponiveis = Array.from(anos).sort();
    },

    processarGraficosComparativos() {
      const configuracoes = [
        {
          chave: "chartOptionsPercentualSolicitacoesRessarcimentoNegadas",
          campo: "percentual_negadas",
          unidadeY: "Percentual (%)",
          maxY: 100,
          valorReferencia: 30,
        },
        {
          chave: "chartOptionsQtdeSolicitacoesIndeferidasPorDanosEletricos",
          campo: "qntd_indeferidas_danos_eletricos",
          unidadeY: "Quantidade",
        },
        {
          chave: "chartOptionsQtdeSolicitacoesRessarcimentoPorDanosEletricos",
          campo: "qntd_ressarcimento_danos_eletricos",
          unidadeY: "Quantidade",
        },
      ];

      configuracoes.forEach((config) => {
        this[config.chave] = this.criarGraficoComparativo(config);
      });
    },

    processarGraficosValores() {
      if (
        !this.dadosValorPendenteSolicitacoesDeferidas ||
        !this.dadosValorPagoRessarcimentoDanosEletricos
      ) {
        return;
      }

      this.processarGraficoValorPendenteDeferido();
      this.processarGraficoValorPagoRessarcimento();
    },

    processarGraficoValorPendenteDeferido() {
      if (
        !this.dadosValorPendenteSolicitacoesDeferidas ||
        Object.keys(this.dadosValorPendenteSolicitacoesDeferidas).length === 0
      ) {
        this.chartOptionsValorTotalPendenteDeferido = {};
        return;
      }

      const dadosAgrupados = this.agruparDadosValores(
        this.dadosValorPendenteSolicitacoesDeferidas,
        "solicitacoes_deferidas",
      );
      this.chartOptionsValorTotalPendenteDeferido = this.criarGraficoValores(
        "Valor (R$)",
        dadosAgrupados,
      );
    },

    processarGraficoValorPagoRessarcimento() {
      if (
        !this.dadosValorPagoRessarcimentoDanosEletricos ||
        Object.keys(this.dadosValorPagoRessarcimentoDanosEletricos).length === 0
      ) {
        this.chartOptionsValorTotalPagoPorRessarcimento = {};
        return;
      }

      const dadosAgrupados = this.agruparDadosValores(
        this.dadosValorPagoRessarcimentoDanosEletricos,
        "valor_pago_ressarcimento_danos_eletricos",
      );
      this.chartOptionsValorTotalPagoPorRessarcimento = this.criarGraficoValores(
        "Valor (R$)",
        dadosAgrupados,
      );
    },

    agruparDadosValores(dados, campo) {
      const dadosAgrupados = {};
      const anos = new Set();

      Object.entries(dados).forEach(([periodo, valor]) => {
        const [mes, ano] = periodo.split("/").map(Number);
        anos.add(ano);

        if (!dadosAgrupados[ano]) dadosAgrupados[ano] = {};
        dadosAgrupados[ano][mes] = valor[campo] || 0;
      });

      return { dadosAgrupados, anos: Array.from(anos).sort() };
    },

    criarGraficoValores(unidadeY, { dadosAgrupados, anos }) {
      const meses = this.obterMesesDisponiveisValores(dadosAgrupados);
      const series = this.criarSeriesValores(dadosAgrupados, anos, meses);

      return {
        chart: {
          type: "column",
          height: 400,
          zoomType: "x",
          animation: {
            duration: 800,
          },
        },
        title: { text: "" },
        xAxis: {
          categories: meses,
          title: { text: "Mês" },
        },
        yAxis: {
          title: { text: unidadeY },
          min: 0,
        },
        plotOptions: {
          series: {
            animation: {
              duration: 800,
            },
          },
        },
        series: series,
        exporting: this.configuracaoExportacao(),
        credits: { enabled: false },
        legend: {
          enabled: true,
          align: "center",
          verticalAlign: "bottom",
        },
        tooltip: {
          formatter: function() {
            return `<b>${this.series.name}</b><br/>${
              this.x
            }: R$ ${this.y.toLocaleString("pt-BR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`;
          },
        },
      };
    },

    obterMesesDisponiveisValores(dadosAgrupados) {
      const meses = new Set();

      Object.values(dadosAgrupados).forEach((dadosAno) => {
        Object.keys(dadosAno).forEach((mes) => meses.add(parseInt(mes)));
      });

      return Array.from(meses)
        .sort((a, b) => a - b)
        .map((mes) => this.obterNomeMes(mes));
    },

    criarSeriesValores(dadosAgrupados, anos, meses) {
      return anos.map((ano, index) => {
        const dadosAno = dadosAgrupados[ano];
        const dados = meses.map((_, mesIndex) => {
          const mes = mesIndex + 1;
          return dadosAno[mes] || 0;
        });

        return {
          name: ano.toString(),
          data: dados,
          color:
            index === 0
              ? this.$vuetify.theme.themes.light.primary
              : this.$vuetify.theme.themes.light.accent,
        };
      });
    },

    criarGraficoComparativo(config) {
      const meses = this.obterMesesDisponiveis();
      const series = this.criarSeriesComparativas(config.campo, meses);

      return {
        chart: {
          type: "column",
          zoomType: "x",
          animation: {
            duration: 800,
          },
        },
        title: { text: "" },
        xAxis: {
          categories: meses,
          title: { text: "Mês" },
        },
        yAxis: {
          title: { text: config.unidadeY },
          min: 0,
          ...(config.maxY && { max: config.maxY }),
          ...(config.valorReferencia && {
            plotLines: [
              {
                value: config.valorReferencia,
                color: this.$vuetify.theme.themes.light.success,
                width: 2,
                zIndex: 10,
                label: {
                  text: `Limite de Referência: ${config.valorReferencia}%`,
                  align: "right",
                  verticalAlign: "top",
                  style: {
                    color: this.$vuetify.theme.themes.light.success,
                    fontWeight: "bold",
                    fontSize: "14px",
                  },
                },
              },
            ],
          }),
        },
        plotOptions: {
          series: {
            animation: {
              duration: 800,
            },
          },
        },
        series: series,
        exporting: this.configuracaoExportacao(),
        credits: { enabled: false },
        legend: {
          enabled: true,
          align: "center",
          verticalAlign: "bottom",
        },
      };
    },

    obterMesesDisponiveis() {
      const meses = new Set();

      Object.values(this.dadosAgrupadosPorAno).forEach((dadosAno) => {
        Object.keys(dadosAno).forEach((mes) => meses.add(parseInt(mes)));
      });

      return Array.from(meses)
        .sort((a, b) => a - b)
        .map((mes) => this.obterNomeMes(mes));
    },

    obterNomeMes(numeroMes) {
      const nomesMeses = [
        "Jan",
        "Fev",
        "Mar",
        "Abr",
        "Mai",
        "Jun",
        "Jul",
        "Ago",
        "Set",
        "Out",
        "Nov",
        "Dez",
      ];
      return nomesMeses[numeroMes - 1] || `Mês ${numeroMes}`;
    },

    criarSeriesComparativas(campo, meses) {
      return this.anosDisponiveis.map((ano, index) => {
        const dadosAno = this.dadosAgrupadosPorAno[ano];
        const dados = meses.map((_, mesIndex) => {
          const mes = mesIndex + 1;
          const dadosMes = dadosAno[mes];
          return dadosMes ? dadosMes[campo] || 0 : 0;
        });

        return {
          name: ano.toString(),
          data: dados,
          color:
            index === 0
              ? this.$vuetify.theme.themes.light.primary
              : this.$vuetify.theme.themes.light.accent,
        };
      });
    },

    configuracaoExportacao() {
      return {
        enabled: true,
        buttons: {
          contextButton: {
            menuItems: [
              "viewFullscreen",
              "separator",
              "downloadPNG",
              "downloadJPEG",
              "downloadPDF",
              "downloadSVG",
              "separator",
              "downloadCSV",
              "downloadXLS",
            ],
          },
        },
      };
    },

    limparGraficos() {
      this.chartOptionsPercentualSolicitacoesRessarcimentoNegadas = {};
      this.chartOptionsQtdeSolicitacoesRessarcimentoPorDanosEletricos = {};
      this.chartOptionsQtdeSolicitacoesIndeferidasPorDanosEletricos = {};
      this.chartOptionsValorTotalPendenteDeferido = {};
      this.chartOptionsValorTotalPagoPorRessarcimento = {};
      this.dadosValorPendenteSolicitacoesDeferidas = {};
      this.dadosValorPagoRessarcimentoDanosEletricos = {};
    },
  },
  watch: {
    activatedTab() {
      this.getDados();
    },
    competencia() {
      this.getDadosComDebounce();
    },
    regionalSelecionada() {
      this.getDadosComDebounce();
    },
    visaoGraficos() {
      this.getDadosComDebounce();
    },
  },
};
</script>

<style scoped></style>
